import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { collection, query, where, orderBy } from "firebase/firestore";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Check, Plus, Trash2 } from "lucide-react";
import { db } from "../../lib/firebase";
import { useFirestoreCollectionOnce } from "../../hooks/useFirestoreCollectionOnce";
import { useDeleteSponsor } from "../../hooks/useSponsorMutations";
import { useSnackbar } from "../../context/SnackbarContext";
import { PageHeader } from "../../components/PageHeader";
import { DataTable, type Column } from "../../components/DataTable";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import type { OverflowAction } from "../../components/phone/OverflowMenu";
import { useIsPhone } from "../../hooks/useIsPhone";
import type { Sponsor } from "@event-planner/shared";

const TIER_COLORS: Record<string, string> = {
  platinum: "#B0BEC5",
  gold: "#FFD54F",
  silver: "#90A4AE",
  bronze: "#BCAAA4",
  custom: "#CE93D8",
};

export function SponsorListPage() {
  const isPhone = useIsPhone();
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();
  const { remove, loading: deleting } = useDeleteSponsor();

  const [deleteTarget, setDeleteTarget] = useState<Sponsor | null>(null);

  const sponsorsQuery = useMemo(
    () =>
      eventId
        ? query(
            collection(db, "sponsors"),
            where("eventId", "==", eventId),
            orderBy("tier", "asc"),
            orderBy("sortOrder", "asc"),
          )
        : null,
    [eventId],
  );

  const {
    data: sponsors,
    loading,
    error,
    refetch: refetchSponsors,
  } = useFirestoreCollectionOnce<Sponsor>(sponsorsQuery);

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await remove(deleteTarget.id);
      showSuccess("Sponsor deleted");
      setDeleteTarget(null);
      refetchSponsors();
    } catch {
      showError("Failed to delete sponsor");
    }
  }

  const columns: Column<Sponsor>[] = [
    {
      id: "name",
      label: "Name",
      render: (sponsor) => sponsor.name,
    },
    {
      id: "tier",
      label: "Tier",
      phone: "chips",
      render: (sponsor) => (
        <Chip
          label={sponsor.tier}
          size="small"
          sx={{
            backgroundColor: TIER_COLORS[sponsor.tier] ?? undefined,
            fontWeight: 500,
            textTransform: "capitalize",
          }}
        />
      ),
    },
    {
      id: "contact",
      label: "Contact",
      phone: "secondary",
      render: (sponsor) => sponsor.contactEmail ?? "—",
    },
    {
      id: "home",
      label: "On Home",
      align: "center",
      render: (sponsor) =>
        sponsor.showOnEventHome ? <Check size={16} /> : null,
    },
    {
      id: "actions",
      label: "",
      align: "right",
      phone: "hidden",
      render: (sponsor) => (
        <Tooltip title="Delete sponsor">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteTarget(sponsor);
            }}
            aria-label={`Delete ${sponsor.name}`}
          >
            <Trash2 size={18} />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  // Phone: "On Home" shows as a worded chip instead of a bare check mark,
  // and delete moves to the row menu.
  const phoneColumns: Column<Sponsor>[] = columns.map((col) =>
    col.id === "contact"
      ? { ...col, render: (sponsor) => sponsor.contactEmail ?? null }
      : col.id === "home"
      ? {
          ...col,
          phone: "chips",
          render: (sponsor) =>
            sponsor.showOnEventHome ? (
              <Chip size="small" variant="outlined" label="On event home" />
            ) : null,
        }
      : col,
  );
  function sponsorRowActions(sponsor: Sponsor): OverflowAction[] {
    return [
      {
        label: "Delete sponsor",
        icon: <Trash2 size={18} />,
        onClick: () => setDeleteTarget(sponsor),
        destructive: true,
      },
    ];
  }

  return (
    <Box>
      <PageHeader
        title="Sponsors"
        action={
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={() => navigate("new")}
          >
            Add Sponsor
          </Button>
        }
      />
      <Alert severity="info" sx={{ mb: 2 }} data-testid="sponsors-beta-notice">
        Sponsors is still under construction during Beta — the page is live
        for previewing the data model, but the attendee-facing experience
        and sponsor-tier polish are not finalized.
      </Alert>
      {!loading && !error && sponsors.length === 0 ? (
        <Typography color="text.secondary">
          No sponsors yet. Add your first sponsor.
        </Typography>
      ) : (
        <DataTable
          columns={isPhone ? phoneColumns : columns}
          data={sponsors}
          loading={loading}
          error={error}
          ariaLabel="Sponsors table"
          getRowKey={(sponsor) => sponsor.id}
          onRowClick={(sponsor) => navigate(sponsor.id)}
          phoneRowActions={sponsorRowActions}
          phoneRowActionsLabel={(sponsor) => `More actions for ${sponsor.name}`}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Sponsor"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
        loading={deleting}
      />
    </Box>
  );
}
